import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { collection, onSnapshot, setDoc, serverTimestamp, updateDoc, doc, deleteDoc, query, where, getDoc, orderBy, limit, Timestamp, getDocs, writeBatch } from 'firebase/firestore';
import { allocateTaskNumber } from '../utils/associationUtils';
import { db } from '../utils/firebase';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import TaskModal from '../components/forms/TaskModal';
import { formatDate, convertDateForSave, getDateRange, validateDateRange } from '../utils/dateHelpers';
import PaginationControls from '../components/common/PaginationControls';
import DateRangeSelector from '../components/common/DateRangeSelector';
import { useAuth } from '../components/auth/AuthContext';
import { collectionPath } from '../utils/firestoreCollections';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS, ACTION_PERMISSIONS } from '../utils/permissions';
import BulkEditModal from '../components/common/BulkEditModal';
import Tooltip from '../components/common/Tooltip';
import { loadcustomers, loadOpportunities, loadOrders, loadContacts, clearEntityCache } from '../utils/searchCache';
import { validateEntity } from '../utils/schemaValidation';
import { handleFirestoreError, PerformanceMonitor } from '../utils/errorHandling';
import { generateTaskId } from '../utils/documentIdUtils';
import { logEntityDeletion, logEntityChanges } from '../utils/logger';
import { useGamification } from '../components/gamification/GamificationContext';
import { useTableState } from '../hooks/useTableState';
import { useFilterBar } from '../hooks/useFilterBar';
import FilterActions from '../components/common/FilterActions';

const PAGE_SIZE = 10;
const STATUS_OPTIONS = ['Open', 'Completed', 'Closed', 'All'];

function isPastOrToday(dateStrOrObj) {
    let date;
    if (!dateStrOrObj) return false;
    if (typeof dateStrOrObj === 'string') {
        date = new Date(dateStrOrObj + 'T00:00:00');
    } else if (dateStrOrObj.seconds) {
        date = new Date(dateStrOrObj.seconds * 1000);
    } else {
        date = new Date(dateStrOrObj);
    }
    const today = new Date();
    today.setHours(0,0,0,0);
    date.setHours(0,0,0,0);
    return date <= today;
}

const TaskPage = ({ user, showToast, querySalesRepId, onTotalCount, onTotalPages }) => {
    const { currentUser } = useAuth();
    const { can } = usePermissions(user);
    const { recordActivity } = useGamification();
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const { sortBy: sortColumn, sortDir: sortDirStr, page, setSort: setTableSort, setPage } = useTableState({ sortBy: 'dueDate', sortDir: 'asc', page: 1 });
    const hasInitialLoadRef = useRef(false);
    const prevFilterCountRef = useRef(0);
    const sort = useMemo(() => ({ column: sortColumn, asc: sortDirStr === 'asc' }), [sortColumn, sortDirStr]);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || 'Open';
    const setActiveTab = useCallback((tab) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (tab === 'Open') { next.delete('tab'); } else { next.set('tab', tab); }
            return next;
        }, { replace: true });
    }, [setSearchParams]);
    const fb = useFilterBar({
        search: '',
        priorityFilter: 'All',
        range: '90',
        customStart: '',
        customEnd: '',
    });
    const navigate = useNavigate();
    
    // Modal state for creating/editing tasks
    const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [orders, setOrders] = useState([]);
    const [contacts, setContacts] = useState([]);

    // Bulk edit state (admin only)
    const canBulkEdit = can(ACTION_PERMISSIONS.BULK_OPERATIONS);
    const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    // Clear selection when filters change
    useEffect(() => { setSelectedTaskIds(new Set()); }, [activeTab, fb.applied]);

    // Calculate date range using utility function (includes 365-day validation)
    const dateRangeFilter = useMemo(() => {
        const { range, customStart, customEnd } = fb.applied;
        // Validate first
        const validation = validateDateRange(range, customStart, customEnd);
        if (!validation.isValid) {
            // Return null if invalid - component will show error
            return null;
        }
        
        // Get date range from utility
        return getDateRange(range, customStart, customEnd);
    }, [fb.applied]);

    useEffect(() => {
        const base = collection(db, collectionPath('tasks'));
        let qRef = base;
        if (querySalesRepId) {
            qRef = query(base, where('uid', '==', querySalesRepId), orderBy('createdAt', 'desc'), limit(1000));
        } else if (!can(FEATURE_PERMISSIONS.TASK_VIEW_ALL) && user?.uid) {
            qRef = query(base, where('uid', '==', user.uid), orderBy('createdAt', 'desc'), limit(1000));
        } else {
            qRef = query(base, orderBy('createdAt', 'desc'), limit(1000));
        }
        const unsub = onSnapshot(qRef, snap => {
            const allTasks = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTasks(allTasks);
        });
        return unsub;
    }, [querySalesRepId, user?.uid, can]);

    // When fetching users:
    useEffect(() => {
        if (!user) return;
        // Load all users if user can view all tasks OR can assign tasks to any user
        // (e.g. Customer Support role needs the full list to populate the TaskModal assignee dropdown)
        if (can(FEATURE_PERMISSIONS.TASK_VIEW_ALL) || can(FEATURE_PERMISSIONS.TASK_ASSIGN_ANY)) {
            const unsub = onSnapshot(collection(db, collectionPath('users')), (snap) => {
                setUsers(snap.docs.map(doc => ({ uid: doc.id, ...doc.data() })));
            });
            return unsub;
        } else {
            getDoc(doc(db, collectionPath('users', user.uid))).then(snap => {
                if (snap.exists()) setUsers([{ uid: snap.id, ...snap.data() }]);
                else setUsers([]);
            });
        }
    }, [user, can]);

    /**
     * User lookup utilities
     */
    const usersById = useMemo(() => {
        const m = new Map();
        users.forEach(u => m.set(u.uid, u));
        return m;
    }, [users]);

    const getRepName = (userId) => {
        const u = usersById.get(userId);
        if (!u) return userId || '-';
        if (u.firstName || u.lastName) {
            // Show "First Last" (trim handles missing one)
            return `${u.firstName || ''} ${u.lastName || ''}`.trim();
        }
        return (
            u.displayName ||
            u.name ||
            u.email ||
            u.uid ||
            '-'
        );
    };

    // When filtering tasks:
    const filteredTasks = useMemo(() => {
        return tasks.filter(task => {
            // Filter by role-based access
            if (!can(FEATURE_PERMISSIONS.TASK_VIEW_ALL) && user?.uid && task.uid !== user.uid) return false;
            
            // Filter by date range — prefer createdAt, fall back to dueDate or lastUpdated
            // so older auto-generated tasks without createdAt are not hidden.
            if (dateRangeFilter && !querySalesRepId) {
                const toDate = (v) => {
                    if (!v) return null;
                    if (typeof v.toDate === 'function') return v.toDate();
                    if (v.seconds) return new Date(v.seconds * 1000);
                    const d = new Date(v);
                    return isNaN(d) ? null : d;
                };
                const dateRef = toDate(task.createdAt)
                    || toDate(task.dueDate)
                    || toDate(task.lastUpdated)
                    || toDate(task.updatedAt);
                if (!dateRef) return true; // no date at all — don't hide it
                if (dateRef < dateRangeFilter.start || dateRef > dateRangeFilter.end) return false;
            }
            
            // Filter by active tab status
            if (activeTab !== 'All') {
                const taskStatus = (task.status || '').toLowerCase();
                if (taskStatus !== activeTab.toLowerCase()) return false;
            }
            
            // Filter by priority
            if (fb.applied.priorityFilter !== 'All') {
                const taskPriority = task.priority || 'Medium';
                if (taskPriority !== fb.applied.priorityFilter) return false;
            }
            
            // Filter by search
            if (!fb.applied.search) return true;
            const searchLower = fb.applied.search.toLowerCase();
            const dueDateStr = task.dueDate ? 
                (task.dueDate.toDate ? task.dueDate.toDate().toLocaleDateString() : new Date(task.dueDate).toLocaleDateString()) : '';
            
            return (
                (task.taskNumberLabel || '').toLowerCase().includes(searchLower) ||
                (task.id || '').toLowerCase().includes(searchLower) ||
                (task.title || '').toLowerCase().includes(searchLower) ||
                (task.description || '').toLowerCase().includes(searchLower) ||
                (task.status || '').toLowerCase().includes(searchLower) ||
                dueDateStr.toLowerCase().includes(searchLower) ||
                getRepName(task.uid).toLowerCase().includes(searchLower)
            );
        });
    }, [tasks, activeTab, fb.applied, dateRangeFilter, can, user?.uid]);
    
    // Calculate counts for each tab (reflects priority + search filters)
    const tabCounts = useMemo(() => {
        const counts = {
            Open: 0,
            Completed: 0,
            Closed: 0,
            All: 0
        };
        
        tasks.forEach(task => {
            // Filter by role-based access
            if (!can(FEATURE_PERMISSIONS.TASK_VIEW_ALL) && user?.uid && task.uid !== user.uid) return;
            
            // Filter by date range (skip in embedded sales rep view)
            // Prefer createdAt, fall back to dueDate/lastUpdated for older tasks.
            if (dateRangeFilter && !querySalesRepId) {
                const toDate = (v) => {
                    if (!v) return null;
                    if (typeof v.toDate === 'function') return v.toDate();
                    if (v.seconds) return new Date(v.seconds * 1000);
                    const d = new Date(v);
                    return isNaN(d) ? null : d;
                };
                const dateRef = toDate(task.createdAt)
                    || toDate(task.dueDate)
                    || toDate(task.lastUpdated)
                    || toDate(task.updatedAt);
                if (!dateRef) return; // no date — include in counts
                if (dateRef < dateRangeFilter.start || dateRef > dateRangeFilter.end) return;
            }
            
            // Filter by priority
            if (fb.applied.priorityFilter !== 'All') {
                const taskPriority = task.priority || 'Medium';
                if (taskPriority !== fb.applied.priorityFilter) return;
            }
            
            // Filter by search
            if (fb.applied.search) {
                const searchLower = fb.applied.search.toLowerCase();
                const matches = (task.title || '').toLowerCase().includes(searchLower) ||
                    (task.description || '').toLowerCase().includes(searchLower) ||
                    (task.status || '').toLowerCase().includes(searchLower) ||
                    getRepName(task.uid).toLowerCase().includes(searchLower);
                if (!matches) return;
            }
            
            const status = (task.status || '').toLowerCase();
            if (status === 'open') counts.Open++;
            else if (status === 'completed') counts.Completed++;
            else if (status === 'closed') counts.Closed++;
            counts.All++;
        });
        
        return counts;
    }, [tasks, dateRangeFilter, can, user?.uid, querySalesRepId, fb.applied]);

    const sortableColumns = [
        'taskNumber',
        'title',
        'dueDate',
        'status',
        'assignedTo',
        'priority',
        'updatedAt',
        'createdAt'
    ];

    const sortedTasks = [...filteredTasks].sort((a, b) => {
        let valA, valB;
        switch (sort.column) {
            case 'taskNumber':
                valA = a.taskNumber || 0;
                valB = b.taskNumber || 0;
                break;
            case 'title':
                valA = (a.title || '').toLowerCase();
                valB = (b.title || '').toLowerCase();
                break;
            case 'dueDate':
                valA = a.dueDate || '';
                valB = b.dueDate || '';
                break;
            case 'status':
                valA = (a.status || '').toLowerCase();
                valB = (b.status || '').toLowerCase();
                break;
            case 'assignedTo':
                // Handle both userId (new) and uid (old) fields
                const userIdA = a.userId || a.uid || a.assignedTo;
                const userIdB = b.userId || b.uid || b.assignedTo;
                valA = (getRepName(userIdA) || '').toLowerCase();
                valB = (getRepName(userIdB) || '').toLowerCase();
                break;
            case 'priority':
                // Sort priority: High > Medium > Low
                const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
                valA = priorityOrder[a.priority || 'Medium'];
                valB = priorityOrder[b.priority || 'Medium'];
                break;
            case 'updatedAt':
                valA = a.updatedAt?.seconds || 0;
                valB = b.updatedAt?.seconds || 0;
                break;
            case 'createdAt':
                valA = a.createdAt?.seconds || 0;
                valB = b.createdAt?.seconds || 0;
                break;
            default:
                valA = '';
                valB = '';
        }
        if (valA < valB) return sort.asc ? -1 : 1;
        if (valA > valB) return sort.asc ? 1 : -1;
        return 0;
    });

    const totalPages = Math.ceil(sortedTasks.length / PAGE_SIZE);
    const paginatedTasks = sortedTasks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // Notify parent component of total count and pages
    useEffect(() => {
        if (onTotalCount) {
            onTotalCount(filteredTasks.length);
        }
        if (onTotalPages) {
            onTotalPages(totalPages);
        }
    }, [filteredTasks.length, totalPages, onTotalCount, onTotalPages]);

    // Reset page when user-driven filters change (track prev applied so initial mount & StrictMode are skipped)
    const prevAppliedRef = useRef(fb.applied);
    useEffect(() => {
        if (prevAppliedRef.current === fb.applied) return; // same reference — skip
        prevAppliedRef.current = fb.applied;
        setPage(1);
    }, [fb.applied, setPage]);

    // Reset page when filtered data count changes (but not on initial load)
    useEffect(() => {
        if (!hasInitialLoadRef.current) {
            if (filteredTasks.length > 0) {
                hasInitialLoadRef.current = true;
                prevFilterCountRef.current = filteredTasks.length;
            }
            return;
        }
        if (filteredTasks.length !== prevFilterCountRef.current) {
            setPage(1);
        }
        prevFilterCountRef.current = filteredTasks.length;
    }, [filteredTasks.length, setPage]);

    const sortArrow = (col) =>
        sort.column === col ? (sort.asc ? ' ▲' : ' ▼') : '';
    const handleSortClick = (col) => {
        if (!sortableColumns.includes(col)) return;
        const newDir = sort.column === col ? (sort.asc ? 'desc' : 'asc') : 'asc';
        setTableSort(col, newDir);
    };

    const handleStatusChange = async (task, newStatus) => {
        try {
            await updateDoc(doc(db, collectionPath('tasks', task.id)), {
                status: newStatus,
                updatedAt: serverTimestamp(),
            });
            logEntityChanges({
                oldObj: task,
                newObj: { ...task, status: newStatus },
                fields: ['status'],
                entityType: 'Task',
                entityId: task.id,
                user,
                action: 'Updated Task Status',
            }).catch(e => console.warn('Failed to log status change:', e));
            if (showToast) showToast({ type: 'success', message: `Status updated to ${newStatus}.` });
        } catch (error) {
            if (showToast) showToast({ type: 'error', message: 'Failed to update status.' });
            console.error('Error updating status:', error);
        }
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;
        try {
            // Get task data before deleting for logging
            const taskDoc = await getDoc(doc(db, collectionPath('tasks', taskId)));
            const taskData = taskDoc.exists() ? taskDoc.data() : null;
            
            // Delete the task document
            await deleteDoc(doc(db, collectionPath('tasks', taskId)));
            
            // Log the deletion to security logs
            if (taskData) {
                logEntityDeletion({
                    user,
                    entityType: 'Task',
                    entityId: taskId,
                    entityName: taskData.taskNumberLabel || taskData.title || taskId,
                    associations: {
                        customerId: taskData.associations?.customerId,
                        opportunityId: taskData.associations?.opportunityId
                    }
                }).catch(e => console.warn('Failed to log deletion:', e));
            }
            
            // Delete all associated notes and notifications
            const notesQuery = query(
                collection(db, collectionPath('notes')),
                where('associations.taskId', '==', taskId)
            );
            const notifsQuery = query(
                collection(db, collectionPath('notifications')),
                where('related.type', '==', 'Task'),
                where('related.id', '==', taskId)
            );
            const [notesSnapshot, notifsSnapshot] = await Promise.all([
                getDocs(notesQuery),
                getDocs(notifsQuery)
            ]);
            const deletePromises = [
                ...notesSnapshot.docs.map(noteDoc =>
                    deleteDoc(doc(db, collectionPath('notes'), noteDoc.id))
                ),
                ...notifsSnapshot.docs.map(nDoc =>
                    deleteDoc(doc(db, collectionPath('notifications'), nDoc.id))
                )
            ];
            await Promise.all(deletePromises);
            
            if (showToast) showToast({ type: 'success', message: 'Task deleted.' });
        } catch (error) {
            if (showToast) showToast({ type: 'error', message: 'Failed to delete task.' });
            console.error('Error deleting task:', error);
        }
    };

    const handleBulkDelete = async () => {
        const count = selectedTaskIds.size;
        if (!window.confirm(`Are you sure you want to delete ${count} task${count !== 1 ? 's' : ''}? This cannot be undone.`)) return;
        setBulkDeleting(true);
        try {
            const ids = [...selectedTaskIds];
            const BATCH_SIZE = 400;
            for (let i = 0; i < ids.length; i += BATCH_SIZE) {
                const chunk = ids.slice(i, i + BATCH_SIZE);
                const batch = writeBatch(db);
                for (const id of chunk) {
                    batch.delete(doc(db, collectionPath('tasks'), id));
                }
                await batch.commit();
            }
            // Log deletions and clean up notes/notifications (fire-and-forget)
            for (const id of ids) {
                const task = tasks.find(t => t.id === id);
                if (task) {
                    logEntityDeletion({
                        user,
                        entityType: 'Task',
                        entityId: id,
                        entityName: task.taskNumberLabel || task.title || id,
                    }).catch(() => {});
                }
                // Delete associated notes and notifications
                const notesQ = query(collection(db, collectionPath('notes')), where('associations.taskId', '==', id));
                const notifsQ = query(collection(db, collectionPath('notifications')), where('related.type', '==', 'Task'), where('related.id', '==', id));
                const [notesSnap, notifsSnap] = await Promise.all([getDocs(notesQ), getDocs(notifsQ)]);
                notesSnap.docs.forEach(d => deleteDoc(doc(db, collectionPath('notes'), d.id)).catch(() => {}));
                notifsSnap.docs.forEach(d => deleteDoc(doc(db, collectionPath('notifications'), d.id)).catch(() => {}));
            }
            setSelectedTaskIds(new Set());
            if (showToast) showToast({ type: 'success', message: `Deleted ${count} task${count !== 1 ? 's' : ''}.` });
        } catch (error) {
            console.error('Bulk delete error:', error);
            if (showToast) showToast({ type: 'error', message: `Failed to delete tasks: ${error.message}` });
        } finally {
            setBulkDeleting(false);
        }
    };

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Assigned Tasks</h2>
            <div className="flex items-center mb-4 space-x-4 flex-wrap gap-y-2">
                {can(FEATURE_PERMISSIONS.TASK_CREATE) && (
                <Tooltip text="Create a new task and assign it to a sales rep">
                    <button
                        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500"
                        onClick={() => setShowCreateTaskModal(true)}
                    >
                        Create Task
                    </button>
                </Tooltip>
                )}
                {canBulkEdit && selectedTaskIds.size > 0 && (
                    <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded px-3 py-1.5">
                        <span className="text-sm font-medium text-indigo-700">{selectedTaskIds.size} selected</span>
                        <button
                            className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                            onClick={() => setShowBulkEditModal(true)}
                            disabled={bulkDeleting}
                        >
                            Bulk Edit
                        </button>
                        {can(FEATURE_PERMISSIONS.TASK_DELETE) && (
                        <button
                            className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            onClick={handleBulkDelete}
                            disabled={bulkDeleting}
                        >
                            {bulkDeleting ? 'Deleting...' : 'Bulk Delete'}
                        </button>
                        )}
                        <button
                            className="px-2 py-1 text-sm text-gray-500 hover:text-gray-700"
                            onClick={() => setSelectedTaskIds(new Set())}
                            disabled={bulkDeleting}
                        >
                            Clear
                        </button>
                    </div>
                )}
                
                {/* Date Range Filter - hidden in embedded sales rep view */}
                {!querySalesRepId && (
                <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-700">Date Range:</span>
                    <DateRangeSelector
                        range={fb.draft.range}
                        setRange={v => fb.setDraft('range', v)}
                        customStart={fb.draft.customStart}
                        setCustomStart={v => fb.setDraft('customStart', v)}
                        customEnd={fb.draft.customEnd}
                        setCustomEnd={v => fb.setDraft('customEnd', v)}
                        defaultRange="90"
                        hideClear
                    />
                </div>
                )}
                
                {/* Priority Filter */}
                <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-700">Priority:</span>
                    <select
                        value={fb.draft.priorityFilter}
                        onChange={(e) => fb.setDraft('priorityFilter', e.target.value)}
                        className="border border-gray-300 rounded px-3 py-2"
                    >
                        <option value="All">All</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                    </select>
                </div>

                <span className="font-medium text-gray-700">Search:</span>
                <input
                    type="text"
                    placeholder="Search tasks..."
                    className={`border rounded px-3 py-2 w-full md:w-96 transition-colors duration-150 ${
                        fb.draft.search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                    }`}
                    value={fb.draft.search}
                    onChange={e => fb.setDraft('search', e.target.value)}
                    onKeyDown={fb.applyOnEnter}
                />

                <FilterActions
                    onApply={fb.apply}
                    onClear={fb.clear}
                    isDirty={fb.isDirty}
                    hasActiveFilters={fb.hasActiveFilters}
                />
                
            </div>
            
            {/* Status Tabs */}
            <div className="mb-6">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        {STATUS_OPTIONS.map(status => (
                            <button
                                key={status}
                                onClick={() => setActiveTab(status)}
                                className={`
                                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                                    ${activeTab === status
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }
                                `}
                            >
                                {status}
                                <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                                    activeTab === status
                                        ? 'bg-indigo-100 text-indigo-600'
                                        : 'bg-gray-100 text-gray-900'
                                }`}>
                                    ({tabCounts[status]})
                                </span>
                            </button>
                        ))}
                    </nav>
                </div>
            </div>
            <div className="overflow-x-auto bg-white rounded-lg shadow mb-6">
                <table className="w-full">
                    <thead>
                        <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50 whitespace-normal">
                            {canBulkEdit && (
                            <th className="px-2 py-3 w-8">
                                <input
                                    type="checkbox"
                                    checked={paginatedTasks.length > 0 && paginatedTasks.every(t => selectedTaskIds.has(t.id))}
                                    onChange={() => {
                                        const pageIds = paginatedTasks.map(t => t.id);
                                        const allSelected = pageIds.every(id => selectedTaskIds.has(id));
                                        setSelectedTaskIds(prev => {
                                            const next = new Set(prev);
                                            pageIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
                                            return next;
                                        });
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    title="Select all on this page"
                                />
                            </th>
                            )}
                            <th
                                className="px-4 py-3 cursor-pointer select-none text-left align-middle break-words"
                                onClick={() => handleSortClick('taskNumber')}
                                style={{ cursor: 'pointer' }}
                            >
                                Task #
                                {sortArrow('taskNumber')}
                            </th>
                            <th
                                className="px-4 py-3 cursor-pointer select-none text-left align-middle break-words pl-6"
                                onClick={() => handleSortClick('title')}
                                style={{ cursor: 'pointer' }}
                            >
                                Title
                                {sortArrow('title')}
                            </th>
                            <th className="px-4 py-3 select-none break-words max-w-[40ch]">
                                Description
                                {/* No sort arrow or click for description */}
                            </th>
                            <th
                                className="px-4 py-3 cursor-pointer select-none whitespace-nowrap"
                                onClick={() => handleSortClick('dueDate')}
                                style={{ cursor: 'pointer' }}
                            >
                                Due Date
                                {sortArrow('dueDate')}
                            </th>
                            <th
                                className="px-4 py-3 cursor-pointer select-none whitespace-nowrap"
                                onClick={() => handleSortClick('status')}
                                style={{ cursor: 'pointer' }}
                            >
                                Status
                                {sortArrow('status')}
                            </th>
                            <th
                                className="px-4 py-3 cursor-pointer select-none whitespace-nowrap"
                                onClick={() => handleSortClick('assignedTo')}
                                style={{ cursor: 'pointer' }}
                            >
                                Assigned To
                                {sortArrow('assignedTo')}
                            </th>
                            <th
                                className="px-4 py-3 cursor-pointer select-none whitespace-nowrap"
                                onClick={() => handleSortClick('priority')}
                                style={{ cursor: 'pointer' }}
                            >
                                Priority
                                {sortArrow('priority')}
                            </th>
                            <th
                                className="px-4 py-3 cursor-pointer select-none whitespace-nowrap"
                                onClick={() => handleSortClick('updatedAt')}
                                style={{ cursor: 'pointer' }}
                            >
                                Last Updated
                                {sortArrow('updatedAt')}
                            </th>
                            <th
                                className="px-4 py-3 cursor-pointer select-none whitespace-nowrap"
                                onClick={() => handleSortClick('createdAt')}
                                style={{ cursor: 'pointer' }}
                            >
                                Created Date
                                {sortArrow('createdAt')}
                            </th>
                            <th className="px-4 py-3 whitespace-nowrap">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y">
                        {paginatedTasks.length === 0 ? (
                            <tr>
                                <td colSpan={canBulkEdit ? 11 : 10} className="text-center py-4 text-gray-500">No tasks found.</td>
                            </tr>
                        ) : (
                            paginatedTasks.map(task => (
                                <tr key={task.id} className={`text-gray-700 hover:bg-gray-50 ${selectedTaskIds.has(task.id) ? 'bg-indigo-50' : ''}`}>
                                    {canBulkEdit && (
                                    <td className="px-2 py-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedTaskIds.has(task.id)}
                                            onChange={() => setSelectedTaskIds(prev => {
                                                const next = new Set(prev);
                                                next.has(task.id) ? next.delete(task.id) : next.add(task.id);
                                                return next;
                                            })}
                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                    </td>
                                    )}
                                    <td>
                                      <Link to={`/tasks/${task.id}`} className="text-indigo-600 hover:underline font-mono">
                                        {task.taskNumberLabel || task.taskNumber || task.id}
                                      </Link>
                                    </td>
                                    <td className="pl-6 max-w-xs truncate font-bold" title={task.title}>
                                        {task.title && task.title.length > 80
                                            ? `${task.title.slice(0, 80)}...`
                                            : task.title}
                                    </td>
                                    <td className="px-4 py-3 text-sm break-words max-w-[40ch] whitespace-nowrap overflow-hidden text-ellipsis" title={task.description}>
                                        {task.description && task.description.length > 80
                                            ? `${task.description.slice(0, 80)}...`
                                            : task.description}
                                    </td>
                                    <td
                                        className={`px-4 py-3 text-sm ${
                                            isPastOrToday(task.dueDate) ? 'font-bold text-red-700' : ''
                                        }`}
                                    >
                                        {formatDate(task.dueDate, currentUser?.dateFormat)}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {can(FEATURE_PERMISSIONS.TASK_EDIT) && (can(FEATURE_PERMISSIONS.TASK_VIEW_ALL) || task.uid === user?.uid) ? (
                                            <select
                                                value={(task.status || 'Open').charAt(0).toUpperCase() + (task.status || 'Open').slice(1).toLowerCase()}
                                                onChange={e => handleStatusChange(task, e.target.value)}
                                                className={`px-2 py-1 rounded-full text-sm font-semibold border-0 cursor-pointer focus:ring-2 focus:ring-indigo-400 ${
                                                    (task.status || '').toLowerCase() === 'completed'
                                                        ? 'bg-green-100 text-green-700'
                                                        : (task.status || '').toLowerCase() === 'closed'
                                                            ? 'bg-gray-200 text-gray-700'
                                                            : 'bg-yellow-100 text-yellow-700'
                                                }`}
                                            >
                                                <option value="Open">Open</option>
                                                <option value="Completed">Completed</option>
                                                <option value="Closed">Closed</option>
                                            </select>
                                        ) : (
                                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                                                (task.status || '').toLowerCase() === 'completed'
                                                    ? 'bg-green-100 text-green-700'
                                                    : (task.status || '').toLowerCase() === 'closed'
                                                        ? 'bg-gray-200 text-gray-700'
                                                        : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                {(task.status || 'Open').charAt(0).toUpperCase() + (task.status || 'Open').slice(1).toLowerCase()}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm">{getRepName(task.uid)}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                            task.priority === 'High' 
                                                ? 'bg-red-100 text-red-800'
                                                : task.priority === 'Low'
                                                    ? 'bg-gray-100 text-gray-800'
                                                    : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {task.priority || 'Medium'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {task.updatedAt && task.updatedAt.seconds
                                            ? formatDate(new Date(task.updatedAt.seconds * 1000), user?.dateFormat)
                                            : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {task.createdAt && task.createdAt.seconds
                                            ? formatDate(new Date(task.createdAt.seconds * 1000), user?.dateFormat)
                                            : '-'}
                                    </td>
                                    <td className="text-sm">
                                        <div className="flex justify-center items-center space-x-2 h-full min-h-[40px]">
                                            {can(FEATURE_PERMISSIONS.TASK_EDIT) && (can(FEATURE_PERMISSIONS.TASK_VIEW_ALL) || task.uid === user?.uid) && (
                                                <>
                                                    <button
                                                        className="text-indigo-600 hover:underline"
                                                        onClick={() => {
                                                            setEditingTask(task);
                                                            setShowCreateTaskModal(true);
                                                        }}
                                                        type="button"
                                                    >
                                                        Edit
                                                    </button>
                                                    {can(FEATURE_PERMISSIONS.TASK_DELETE) && (
                                                        <button
                                                            className="text-red-700 hover:underline"
                                                            onClick={() => handleDeleteTask(task.id)}
                                                            type="button"
                                                        >
                                                            Delete
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
            />
            
            {/* Create/Edit Task Modal */}
            {showCreateTaskModal && (
                <TaskModal
                    salesReps={users.filter(u => u.uid)}
                    initial={editingTask}
                    isAdmin={can(FEATURE_PERMISSIONS.TASK_VIEW_ALL)}
                    customers={customers}
                    opportunities={opportunities}
                    orders={orders}
                    contacts={contacts}
                    onLoadCustomers={async () => {
                        if (customers.length === 0) {
                            const loaded = await loadcustomers();
                            setCustomers(loaded);
                        }
                    }}
                    onLoadOpportunities={async () => {
                        if (opportunities.length === 0) {
                            const loaded = await loadOpportunities();
                            setOpportunities(loaded);
                        }
                    }}
                    onLoadOrders={async () => {
                        if (orders.length === 0) {
                            const loaded = await loadOrders();
                            setOrders(loaded);
                        }
                    }}
                    onLoadContacts={async () => {
                        if (contacts.length === 0) {
                            const loaded = await loadContacts();
                            setContacts(loaded);
                        }
                    }}
                    onSave={async (taskData) => {
                        try {
                            if (editingTask) {
                                // Update existing task
                                let dueDateTimestamp;
                                if (taskData.dueDate instanceof Date) {
                                    dueDateTimestamp = Timestamp.fromDate(taskData.dueDate);
                                } else if (typeof taskData.dueDate === 'string') {
                                    dueDateTimestamp = convertDateForSave(taskData.dueDate);
                                } else if (taskData.dueDate?.toDate) {
                                    dueDateTimestamp = taskData.dueDate;
                                }
                                
                                await updateDoc(doc(db, collectionPath('tasks'), editingTask.id), {
                                    ...taskData,
                                    dueDate: dueDateTimestamp,
                                    updatedAt: serverTimestamp(),
                                });
                                
                                // Log the update
                                logEntityChanges({
                                    oldObj: editingTask,
                                    newObj: { ...editingTask, ...taskData, dueDate: dueDateTimestamp },
                                    fields: ['title', 'status', 'priority', 'uid', 'dueDate', 'customerId', 'opportunityId', 'description'],
                                    entityType: 'Task',
                                    entityId: editingTask.id,
                                    user,
                                    action: 'Updated',
                                }).catch(e => console.warn('Failed to log task update:', e));

                                // Gamification: Track task completion (with anti-abuse validation)
                                if (taskData.status === 'completed' && editingTask.status !== 'completed') {
                                    try {
                                        // Check if completed on time
                                        const dueDate = dueDateTimestamp?.toDate ? dueDateTimestamp.toDate() : new Date(taskData.dueDate);
                                        const today = new Date();
                                        today.setHours(0,0,0,0);
                                        dueDate.setHours(0,0,0,0);
                                        
                                        if (today <= dueDate) {
                                            await recordActivity('TASK_COMPLETED_ON_TIME', {
                                                entityId: editingTask.id,
                                                source: 'manual'
                                            });
                                        } else {
                                            await recordActivity('TASK_OVERDUE_COMPLETED', {
                                                entityId: editingTask.id,
                                                source: 'manual'
                                            });
                                        }
                                    } catch (gamErr) {
                                        console.warn('Gamification tracking failed:', gamErr);
                                    }
                                }
                                
                                if (showToast) {
                                    showToast({ type: 'success', message: 'Task updated successfully' });
                                }
                            } else {
                                // Create new task - Generate task ID and number
                                const seq = await allocateTaskNumber();
                                
                                const taskDocId = generateTaskId(seq);
                                
                                // Convert dueDate to Timestamp
                                let dueDateTimestamp;
                                if (taskData.dueDate instanceof Date) {
                                    dueDateTimestamp = Timestamp.fromDate(taskData.dueDate);
                                } else if (typeof taskData.dueDate === 'string') {
                                    dueDateTimestamp = convertDateForSave(taskData.dueDate);
                                } else if (taskData.dueDate?.toDate) {
                                    dueDateTimestamp = taskData.dueDate;
                                }
                                
                                // Create task document
                                await setDoc(doc(db, collectionPath('tasks'), taskDocId), {
                                    ...taskData,
                                    taskNumber: seq,
                                    taskNumberLabel: taskDocId,
                                    status: (taskData.status || 'open').toLowerCase(),
                                    priority: taskData.priority || 'Medium',
                                    dueDate: dueDateTimestamp,
                                    createdAt: serverTimestamp(),
                                    updatedAt: serverTimestamp(),
                                });
                                
                                // Log the creation
                                logEntityChanges({
                                    oldObj: null,
                                    newObj: { ...taskData, taskNumber: seq, taskNumberLabel: taskDocId },
                                    fields: ['title', 'status', 'priority', 'uid', 'dueDate', 'customerId', 'opportunityId', 'description'],
                                    entityType: 'Task',
                                    entityId: taskDocId,
                                    user,
                                    action: 'Created',
                                }).catch(e => console.warn('Failed to log task creation:', e));

                                // Gamification: Track task creation (with anti-abuse validation)
                                try {
                                    await recordActivity('TASK_CREATED', {
                                        entityId: taskDocId,
                                        source: 'manual',
                                        entityData: { title: taskData.title }
                                    });
                                } catch (gamErr) {
                                    console.warn('Gamification tracking failed:', gamErr);
                                }
                                
                                if (showToast) {
                                    showToast({ type: 'success', message: 'Task created successfully' });
                                }
                            }
                            setShowCreateTaskModal(false);
                            setEditingTask(null);
                        } catch (error) {
                            console.error('Error saving task:', error);
                            if (showToast) {
                                showToast({ type: 'error', message: `Failed to save task: ${error.message}` });
                            }
                        }
                    }}
                    onClose={() => {
                        setShowCreateTaskModal(false);
                        setEditingTask(null);
                    }}
                />
            )}
            {showBulkEditModal && (
                <BulkEditModal
                    entityType="task"
                    selectedItems={tasks.filter(t => selectedTaskIds.has(t.id))}
                    users={users}
                    user={user}
                    onClose={() => setShowBulkEditModal(false)}
                    onComplete={(msg) => {
                        setShowBulkEditModal(false);
                        setSelectedTaskIds(new Set());
                        if (showToast) showToast({ type: 'success', message: msg });
                    }}
                />
            )}
        </div>
    );
};

export default TaskPage;
